/**
 * @file: util.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { VehicleInspectionTask } from '@euler/model/entity/VehicleInspectionTask';
import { InspectionTaskStatus } from '@euler/model/enum';
import { ReportContentType } from '@euler/model/ReportContentType';
import { TaskListItem, TaskListItemTarget } from '@euler/model/TaskListItem';
import {
  getTaskStatusInfoFromServiceRecord,
  getTaskStatusInfoFromTask,
} from '@euler/model/TaskStatusInfo';
import { VehicleServiceRecord } from '@euler/model/VehicleServiceRecord';
import { ReportTabKey } from '@euler/model/viewmodel';
import { WithExtraVehicleModelProps } from '@euler/model/WithExtraVehicleModelProps';

export function taskListItemFromTask(
  task: VehicleInspectionTask,
): TaskListItem {
  const statusInfo = getTaskStatusInfoFromTask(task);
  const target: TaskListItemTarget =
    task.status === InspectionTaskStatus.Finished && task.serviceRecordId
      ? {
          type: 'service-record',
          serviceRecordId: task.serviceRecordId,
        }
      : {
          type: 'task',
          taskNo: task.taskNo,
        };

  return {
    ...target,
    id: task.taskNo,
    isMainTask: task.isMainTask,
    isEntryTask: task.isEntryTask,
    hasChildTasks: task.hasChildTasks,
    parentTaskId: task.parentTaskId,
    parentTaskNo: task.parentTaskNo,
    parentOrderId: task.parentOrderId,
    parentOrderNo: task.parentOrderNo,
    orgId: task.orgId,
    storeId: task.storeId,
    orderNo: task.orderNo,
    reportNo: task.reportNo,
    licensePlateNo: task.licensePlateNo,
    vin: task.vin,
    tags: task.tags,
    subscribed: task.observes != null && task.observes.length > 0,
    serviceAgentId: task.serviceAgentId,
    serviceAgentName: task.serviceAgentName,
    customerName: task.customerName,
    vehicleBrandName: task.vehicleBrandName,
    vehicleName: task.vehicleName,
    vehicleMileage: task.vehicleMileage,
    vehicleImageUrl: task.vehicleImageUrl ?? undefined,
    vehicleBrandLogo: task.vehicleBrandLogo ?? undefined,
    vehicleManufacturer: task.vehicleManufacturer ?? undefined,
    vehicleFuelType: task.vehicleFuelType ?? undefined,
    vehicleModel: task.vehicleModel ?? undefined,
    vehicleModelYear: task.vehicleModelYear ?? undefined,
    vehicleDisplacement: task.vehicleDisplacement ?? undefined,
    vehicleTrimLevel: task.vehicleTrimLevel ?? undefined,
    orderedAt: task.createdAt,
    estimatedFinishTime: task.estimatedFinishTime ?? undefined,
    isRollback: task.isRollback ?? false,
    rollbackAt: task.rollbackAt ?? undefined,
    rollbackReason: task.rollbackReason ?? undefined,
    canceledAt: task.canceledAt ?? undefined,
    cancelReason: task.cancelReason ?? undefined,
    isFirstTimeOrder: task.isFirstTimeOrder,
    ...statusInfo,
  };
}

export function taskListItemFromServiceRecord(
  record: WithExtraVehicleModelProps<VehicleServiceRecord>,
): TaskListItem {
  const statusInfo = getTaskStatusInfoFromServiceRecord(record);
  return {
    id: record._id,
    isMainTask: record.isMainRecord,
    isEntryTask: record.isMainRecord,
    hasChildTasks: record.hasChildRecords ?? false,
    type: 'service-record',
    serviceRecordId: record._id,
    orgId: record.orgId!,
    storeId: record.storeId!,
    orderNo: record.orderNo!,
    reportNo: record.reportNo!,

    licensePlateNo: record.licensePlateNo,
    vin: record.vin,
    tags: record.tags,
    subscribed: record.subscribed!,
    serviceAgentName: record.serviceAgentName!,
    customerName: record.customerName,
    vehicleBrandName: record.vehicleBrand,
    vehicleName: record.vehicleName,
    vehicleMileage: record.mileage,
    vehicleImageUrl: record.vehicleImageUrl ?? undefined,
    vehicleBrandLogo: record.vehicleBrandLogo ?? undefined,
    vehicleManufacturer: record.vehicleManufacturer ?? undefined,
    vehicleFuelType: record.vehicleFuelType ?? undefined,
    vehicleModel: record.vehicleModel ?? undefined,
    vehicleModelYear: record.vehicleModelYear ?? undefined,
    vehicleDisplacement: record.vehicleDisplacement ?? undefined,
    vehicleTrimLevel: record.vehicleTrimLevel ?? undefined,
    orderedAt: record.orderedAt!,
    estimatedFinishTime: undefined,
    canceledAt: undefined,
    cancelReason: undefined,
    isFirstTimeOrder: record.isFirstTimeOrder,
    pendingIssueCount: record.pendingIssueCount,
    maintenancePlanItemCount: record.maintenancePlanItemCount,

    ...statusInfo,
  };
}

export function reportTabKeyToReportContentType(
  tab: ReportTabKey,
): ReportContentType {
  switch (tab) {
    case 'preInspection':
      return ReportContentType.PreInspection;
    case 'inspection':
      return ReportContentType.Inspection;
    case 'construction':
      return ReportContentType.Construction;
    case 'deliveryCheck':
      return ReportContentType.DeliveryCheck;
    case 'samplingCheck':
      return ReportContentType.SamplingCheck;
  }
}

/**
 * Inverse of {@link reportTabKeyToReportContentType}. The overall
 * (`Default`) report maps to no specific tab — the Report screen then
 * lands on its first available tab.
 */
export function reportContentTypeToReportTabKey(
  type: ReportContentType,
): ReportTabKey | undefined {
  switch (type) {
    case ReportContentType.PreInspection:
      return 'preInspection';
    case ReportContentType.Inspection:
      return 'inspection';
    case ReportContentType.Construction:
      return 'construction';
    case ReportContentType.DeliveryCheck:
      return 'deliveryCheck';
    case ReportContentType.SamplingCheck:
      return 'samplingCheck';
    case ReportContentType.Default:
      return undefined;
  }
}
